'use client';

import { useMemo, useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog.tsx';
import { Badge } from '../ui/badge.tsx';
import { Calendar, Clock, MapPin, User, FileText } from 'lucide-react';
import { useGetClassSchedule } from '../../api/events/eventQueries.js';
import { LoadingSpinner } from '../ui/loading-spinner.tsx';
import { useGetStaffById } from '../../api/staff/staffQueries.js';

export default function ClassScheduleOverview({
  classId = '',
  setClassSchedule = () => {},
}) {
  const [selectedEvent, setSelectedEvent] = useState(null);

  // Get current week dates
  const getCurrentWeekDates = () => {
    const today = new Date();
    const currentDay = today.getDay();
    const diff = today.getDate() - currentDay + (currentDay === 0 ? -6 : 1); // Adjust for Sunday
    const monday = new Date(today.setDate(diff));

    const weekDates = [];
    for (let i = 0; i < 7; i++) {
      const date = new Date(monday);
      date.setDate(monday.getDate() + i);
      weekDates.push(date);
    }
    return weekDates;
  };

  const weekDates = getCurrentWeekDates();
  const timeSlots = [
    { hour: 6, label: '6:00 AM' },
    { hour: 9, label: '9:00 AM' },
    { hour: 12, label: '12:00 PM' },
    { hour: 15, label: '3:00 PM' },
    { hour: 18, label: '6:00 PM' },
  ];

  const {
    data: events,
    isLoading,
    isError,
  } = useGetClassSchedule(
    weekDates[0].toISOString().split('T')[0],
    weekDates[6].toISOString().split('T')[0],
    classId
  );

  useMemo(() => {
    if (events) {
      setClassSchedule(events);
    }
  }, [events, setClassSchedule]);

  // Filter events for current week
  const currentWeekEvents = useMemo(() => {
    if (isLoading || isError || !events?.data) {
      return [];
    }
    return events.data.filter((event) => {
      const eventDate = new Date(event.eventDate);
      return weekDates.some(
        (date) => eventDate.toDateString() === date.toDateString()
      );
    });
  }, [events?.data, isLoading, isError, weekDates]);

  // Get events for a specific day
  const getEventsForDay = (date) => {
    return currentWeekEvents.filter((event) => {
      const eventDate = new Date(event.eventDate);
      return eventDate.toDateString() === date.toDateString();
    });
  };

  // Calculate event position with proportional positioning within 3-hour blocks
  const getEventPosition = (event) => {
    const startTime = new Date(event.startTime);
    const endTime = new Date(event.endTime);
    const startHour = startTime.getHours();
    const startMinutes = startTime.getMinutes();
    const endHour = endTime.getHours();
    const endMinutes = endTime.getMinutes();

    // Convert time to minutes from 6am
    const startTotalMinutes = (startHour - 6) * 60 + startMinutes;
    const endTotalMinutes = (endHour - 6) * 60 + endMinutes;

    // Each 3-hour block is 80px, so each minute is 80px / 180 minutes = 0.444px per minute
    const pixelsPerMinute = 80 / 180;

    const top = Math.max(0, startTotalMinutes * pixelsPerMinute);
    const height = Math.max(
      20, // Minimum height for visibility
      (endTotalMinutes - startTotalMinutes) * pixelsPerMinute
    );

    return {
      top,
      height,
    };
  };

  const getEventTypeColor = (type) => {
    switch (type) {
      case 'Event':
        return 'bg-purple-100 text-purple-800 border-purple-200';
      case 'Task':
        return 'bg-blue-100 text-blue-800 border-blue-200';
      case 'Schedule':
        return 'bg-green-100 text-green-800 border-green-200';
      default:
        return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const formatTime = (date) => {
    return new Date(date).toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
    });
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="text-sm text-gray-500">
          Current Week: {weekDates[0].toLocaleDateString()} -{' '}
          {weekDates[6].toLocaleDateString()}
        </div>
      </div>

      {/* Weekly Calendar Grid */}
      {isError ? (
        <div className="w-full border border-red-500 flex flex-col items-center justify-center p-4 rounded">
          <p className="text-red-500 text-sm">
            Unable to load events. Please try again later.
          </p>
        </div>
      ) : (
        <div>
          {isLoading ? (
            <LoadingSpinner />
          ) : (
            <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
              <div className="grid grid-cols-8 border-b border-gray-200">
                <div className="p-4 bg-gray-50 font-medium text-gray-700">
                  Time
                </div>
                {weekDates.map((date, index) => (
                  <div key={index} className="p-4 bg-gray-50 text-center">
                    <div className="font-medium text-gray-900">
                      {date.toLocaleDateString('en-US', { weekday: 'short' })}
                    </div>
                    <div className="text-sm text-gray-500 mt-1">
                      {date.getDate()}
                    </div>
                  </div>
                ))}
              </div>

              <div className="grid grid-cols-8">
                {/* Time column */}
                <div className="border-r border-gray-200">
                  {timeSlots.map((slot, index) => (
                    <div
                      key={index}
                      className="h-20 p-2 border-b border-gray-100 text-sm text-gray-600 font-medium bg-gray-50"
                    >
                      {slot.label}
                    </div>
                  ))}
                </div>

                {/* Day columns */}
                {weekDates.map((date, dayIndex) => (
                  <div
                    key={dayIndex}
                    className="border-r border-gray-200 relative"
                  >
                    {/* Hourly grid lines within each 3-hour block */}
                    {timeSlots.map((slot, slotIndex) => (
                      <div
                        key={slotIndex}
                        className="h-20 border-b border-gray-100 relative"
                      >
                        {/* Hourly divisions within each 3-hour block */}
                        <>
                          <div className="absolute top-1/3 left-0 right-0 h-px bg-gray-200 opacity-50" />
                          <div className="absolute top-2/3 left-0 right-0 h-px bg-gray-200 opacity-50" />
                        </>
                      </div>
                    ))}

                    {/* Events for this day */}
                    {getEventsForDay(date).map((event) => {
                      const position = getEventPosition(event);
                      return (
                        <div
                          key={event.eventId}
                          className={`absolute left-1 right-1 rounded-md border cursor-pointer hover:shadow-md transition-shadow z-10 p-1 ${getEventTypeColor(
                            event.eventType
                          )}`}
                          style={{
                            top: `${position.top + 2}px`,
                            height: `${Math.max(position.height - 4, 16)}px`,
                          }}
                          onClick={() => setSelectedEvent(event)}
                        >
                          <div className="text-xs font-medium truncate">
                            {event.eventTitle}
                          </div>
                          {position.height > 30 && (
                            <div className="text-xs opacity-75 truncate">
                              {formatTime(event.startTime)}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Event Details Modal */}
      <EventDialog
        selectedEvent={selectedEvent}
        setSelectedEvent={setSelectedEvent}
        getEventTypeColor={getEventTypeColor}
        formatTime={formatTime}
      />
    </div>
  );
}

function EventDialog({
  selectedEvent,
  setSelectedEvent,
  getEventTypeColor,
  formatTime,
}) {
  const {
    data: staff,
    isLoading,
    isError,
  } = useGetStaffById(selectedEvent ? selectedEvent.assignee : '');
  return (
    <Dialog open={!!selectedEvent} onOpenChange={() => setSelectedEvent(null)}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Calendar className="h-5 w-5 text-purple-600" />
            Event Details
          </DialogTitle>
        </DialogHeader>

        {selectedEvent && (
          <div className="space-y-4">
            <div>
              <h3 className="font-semibold text-lg">
                {selectedEvent.eventTitle}
              </h3>
              <Badge className={getEventTypeColor(selectedEvent.eventType)}>
                {selectedEvent.eventType}
              </Badge>
            </div>

            <div className="space-y-3">
              <div className="flex items-center gap-2 text-sm">
                <Clock className="h-4 w-4 text-gray-500" />
                <span>
                  {formatTime(selectedEvent.startTime)} -{' '}
                  {formatTime(selectedEvent.endTime)}
                </span>
              </div>

              <div className="flex items-center gap-2 text-sm">
                <Calendar className="h-4 w-4 text-gray-500" />
                <span>
                  {new Date(selectedEvent.eventDate).toLocaleDateString()}
                </span>
              </div>

              <div className="flex items-center gap-2 text-sm">
                <MapPin className="h-4 w-4 text-gray-500" />
                <span>{selectedEvent.location}</span>
              </div>

              {selectedEvent.assignee && (
                <div className="flex items-center gap-2 text-sm">
                  <User className="h-4 w-4 text-gray-500" />
                  {isLoading ? (
                    <LoadingSpinner />
                  ) : isError || staff === undefined ? (
                    <p>An error occured</p>
                  ) : (
                    <span>
                      Assigned to: {staff.firstName} {staff.lastName}
                    </span>
                  )}
                </div>
              )}

              <div className="flex items-start gap-2 text-sm">
                <FileText className="h-4 w-4 text-gray-500 mt-0.5" />
                <span>{selectedEvent.description}</span>
              </div>

              {selectedEvent.recurring && (
                <div className="text-sm text-purple-600 bg-purple-50 p-2 rounded">
                  <strong>Recurring:</strong> {selectedEvent.recurrenceRule}
                </div>
              )}
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
